import {useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import * as yup from 'yup'
import {
  AuthenticatePayload,
  AuthenticateResponse,
  AuthenticationTypeProps,
} from '../props_reusable/authentication'
import authenticationService from '../services/authentication.service'
import {RootState} from '../store_redux'
import utils from '../utils'

const validationSchema = yup.object({
  showOrderCode: yup.boolean(),
  showPostalCode: yup.boolean(),
  orderCode: yup.string().when('showOrderCode', {
    is: true,
    then: yup.string().required(),
  }),
  postalCode: yup.string().when('showPostalCode', {
    is: true,
    then: yup.string().required(),
  }),
})
type LoaderState = {
  message: string
  loading: boolean
}
const useLoginForm = (authenticateType: AuthenticationTypeProps) => {
  const labels = useSelector((state: RootState) => state.labels)
  const [loader, setLoader] = useState<LoaderState>({
    loading: false,
    message: '',
  })
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orderGUID = searchParams.get('orderGUID') || ''

  const inputs = useMemo(
    () => ({
      orderCode: authenticateType === 'PostCodeAndOrderCode',
      postalCode: /PostCodeAndOrderCode|PostCode/i.test(authenticateType),
    }),
    [authenticateType],
  )

  const initialValues = useMemo(
    () => ({
      orderCode: '',
      postalCode: '',
      showOrderCode: inputs.orderCode,
      showPostalCode: inputs.postalCode,
    }),
    [inputs.orderCode, inputs.postalCode],
  )

  const handleLoader = (isLoading: boolean, message = '') => {
    setLoader({loading: isLoading, message: message || ''})
  }

  const setTokenInStorage = ({token}: AuthenticateResponse) => {
    utils.setTokenInStorage(orderGUID, token)
    navigate(`/my-orders?orderGUID=${orderGUID}`)
  }

  const handleError = (error: Error) => {
    const msg = utils.getErrorMsg(labels.errors.authenticate, error.message)
    handleLoader(false, msg)
  }

  const generateJWTToken = (payload?: Partial<AuthenticatePayload>) => {
    authenticationService
      .authenticate<AuthenticateResponse>(orderGUID, payload)
      .then(setTokenInStorage)
      .catch(handleError)
  }

  const onSubmit = (values: typeof initialValues) => {
    handleLoader(true)
    const payload = {} as AuthenticatePayload
    if (values.showOrderCode) payload.clientOrderCode = values.orderCode
    if (values.showPostalCode) payload.postCode = values.postalCode
    generateJWTToken(payload)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  useEffect(() => {
    if (authenticateType === 'None') {
      handleLoader(true)
      generateJWTToken()
    }
    // eslint-disable-next-line
  }, [authenticateType])

  return {inputs, loader, formik, labels, setLoader}
}

export default useLoginForm
